/* eslint-disable no-console */
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

import { configPublic } from '@src/config.public'
import { getIsServer } from '@src/utils/common.util'

import { storage } from './storage.util'

const levels = {
  values: {
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
  },
  colors: {
    debug: 'SteelBlue',
    info: 'ForestGreen',
    warn: '#573D10',
    error: 'DarkRed',
  },
  labels: {
    20: 'debug',
    30: 'info',
    40: 'warn',
    50: 'error',
  },
} as const

type LogLevelValue = keyof typeof levels.labels
type LogLevelLabel = keyof typeof levels.values
interface LogLevel {
  value: LogLevelValue
  label: LogLevelLabel
}
type LogFn = (message: string, messageContext?: object | Error, error?: unknown | Error) => void

/**
 * Escape special characters in a string to use it in a regular expression.
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?{}()|[\]\\]/g, '\\$&')
}

/**
 * Create a logging function for the given log level.
 */
function logFactory(level: LogLevel): LogFn {
  return function (message, messageContext, error) {
    if (messageContext instanceof Error) {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      error = error || messageContext
      messageContext = getEnumerableError(messageContext)
    }
    const redactedMessageContext = redactSensitiveFields(messageContext)
    const isServer = getIsServer()
    let { logLevelClient, logsVisibleInConsole } = configPublic.logs
    const { logLevelServer } = configPublic.logs

    // Override log level if set in local storage
    logLevelClient = storage.local.get('logLevelClient') ?? logLevelClient
    logsVisibleInConsole =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      storage.local.get('logsVisibleInConsole') || logsVisibleInConsole || isServer

    // Determine the minimum log level to display
    const levelMinLabel = (isServer ? logLevelServer : logLevelClient) as LogLevelLabel
    const levelMinValue = levels.values[levelMinLabel]

    // Don't send logs that are below the configured log level
    if (level.value < levelMinValue) return

    // Handle sending logs to the console
    if (logsVisibleInConsole) {
      if (isServer) {
        // Send all content in a single string for server logs,
        // because additional log arguments get dropped in the server console
        const context = messageContext
          ? ` | messageContext: ${JSON.stringify(redactedMessageContext)}`
          : ''
        console[level.label](`${level.label.toUpperCase()}: ${message}${context}`)
      } else {
        if (level.value >= levels.values.error) {
          // Error logs don't need to be pretty because the browser already makes them pretty
          console.error(message, messageContext)
        } else {
          // Make all the other logs pretty
          console[level.label](
            `%c${level.label.toUpperCase()}:`,
            `color: ${levels.colors[level.label]}; font-weight: bold`,
            message,
            messageContext
          )
        }
      }
    }

    // Don't send logs to Datadog from the server
    if (isServer) return

    // Forward non-error browser logs to DataDog
    if (level.value < levels.values.error) {
      datadogLogs.logger[level.label](
        message,
        { messageContext: redactedMessageContext },
        error as Error
      )
    }

    // NOTE: DataDog is configured to auto forward errors, which includes calls to console.error.
    // We only want to explicitly send error logs to DataDog if logs are not visible in the console.
    if (!logsVisibleInConsole && level.value >= levels.values.error) {
      datadogLogs.logger.error(message, { messageContext: redactedMessageContext }, error as Error)

      // NOTE: calling console.error produces an actual error in DataDog, but calling
      // datadogLogs.logger.error does not. In order to properly report errors in DataDog, we
      // manually send an error with datadogRum.
      datadogRum.addError(error, { messageContext: redactedMessageContext })
    }
  }
}

/**
 * Logger object with methods for each log level and utility methods to control log visibility.
 */
export const log = {
  error: logFactory({ value: levels.values.error, label: 'error' }),
  warn: logFactory({ value: levels.values.warn, label: 'warn' }),
  info: logFactory({ value: levels.values.info, label: 'info' }),
  debug: logFactory({ value: levels.values.debug, label: 'debug' }),

  /**
   * Control whether to show logs in the console or not.
   * Calling without an argument toggles the config controlling log visibility.
   */
  setLogVisibility(show?: boolean) {
    const shouldShow = show ?? !storage.local.get('logsVisibleInConsole')
    storage.local.set('logsVisibleInConsole', shouldShow)
    return shouldShow
  },

  /**
   * Control the client side log level.
   */
  setLogLevelClient(level: LogLevelLabel) {
    if (levels.values[level]) {
      storage.local.set('logLevelClient', level)
    } else {
      log.error('You must provide a valid log level:', Object.keys(levels.values))
    }
  },

  /**
   * Get the current values of configs used to determine log levels and visibility.
   */
  getStatus() {
    return {
      logsVisibleInConsole: storage.local.get('logsVisibleInConsole'),
      logLevelClientOverride: storage.local.get('logLevelClient'),
      logLevelClient: configPublic.logs.logLevelClient,
      logLevelServer: configPublic.logs.logLevelServer,
    }
  },
}

// Expose logger globally
// @ts-expect-error - This is exposed globally for use in the browser console,
// but we don't want it used globally in this codebase, so it is intentionally not typed.
globalThis.log = log
globalThis.appVersion = configPublic.general.appVersion

/**
 * Intercept default browser console methods in order to send all logging through our custom logger.
 * This was necessary to avoid reporting duplicate logs to DataDog when logsVisibleInConsole was true.
 * We have set forwardConsoleLogs: [] (i.e. none) in the DataDog logs SDK.
 * Instead, we are manually sending logs to DataDog.
 */
export function interceptConsole() {
  if (getIsServer()) return
  const console = window.console
  const methods = ['trace', 'debug', 'log', 'info', 'warn', 'error'] as const
  methods.forEach(intercept)

  function intercept(method: (typeof methods)[number]) {
    const original = console[method]
    const targetMethod = (Object.keys(levels.values).find((l) => l === method) ??
      'info') as LogLevelLabel
    const target = log[targetMethod]
    console[method] = function (...args) {
      const [firstMessage, ...messages] = args
      const normalizedArgs: Parameters<LogFn> =
        typeof firstMessage === 'string'
          ? [firstMessage, { interceptedArgs: messages }]
          : [targetMethod.toUpperCase(), { interceptedArgs: args }]

      if (storage.local.get('logsVisibleInConsole')) {
        // Call original console method
        original.apply(console, args)
      } else {
        // Call custom logger
        target.apply(log, normalizedArgs)
      }
    }
  }
}

export function getEnumerableError<T>(error: T): object | T {
  if (!(error instanceof Error)) {
    return error
  }

  const serializedError = {
    name: error.name,
    message: error.message,
    ...Object.getOwnPropertyNames(error).reduce((acc: any, key) => {
      acc[key] = (error as any)[key]
      return acc
    }, {}),
    stack: error.stack,
  }

  return serializedError
}

const sensitiveFieldPatterns = configPublic.logs.sensitiveFieldPatterns as string[]
const safeFieldPatterns = configPublic.logs.safeFieldPatterns as string[]

/**
 * Determines if an object key is sensitive based on its name.
 */
export function isSensitiveKey(key: string) {
  const doesKeyMatchSensitivePattern = sensitiveFieldPatterns.some((pattern) =>
    new RegExp(escapeRegExp(pattern), 'ig').test(key.toLowerCase())
  )
  const isKeySafe = safeFieldPatterns.some((pattern) =>
    new RegExp(escapeRegExp(pattern), 'g').test(key)
  )
  return doesKeyMatchSensitivePattern && !isKeySafe
}

/**
 * Redact sensitive data in logs.
 */
export function redactSensitiveFields<T>(data: T): T {
  function replacer(key: string, value: unknown) {
    return isSensitiveKey(key) ? `[REDACTED]` : value
  }

  let result
  try {
    result = JSON.parse(JSON.stringify(data, replacer)) as T
  } catch (error) {
    result = data
  }
  return result
}
